package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.NotFoundException
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.model.WorkplaceGroups
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.team.BulkEditPlanner
import com.evoapps.evoshift.domain.team.BulkEmployeeEdit
import com.evoapps.evoshift.domain.team.BulkEmployeeEditUseCase
import com.evoapps.evoshift.domain.team.BulkPlan
import com.evoapps.evoshift.domain.team.BulkTarget
import com.evoapps.evoshift.domain.team.BulkUndo
import com.evoapps.evoshift.domain.team.ContractEdit
import com.evoapps.evoshift.domain.team.PayRaise
import com.evoapps.evoshift.domain.team.SkillsEdit
import com.evoapps.evoshift.domain.usecase.user.GetAssignableEmployeesUseCase
import com.evoapps.evoshift.domain.usecase.GetWorkplaceUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.owner.GetWorkplaceGroupsUseCase
import com.evoapps.evoshift.widgets.parseIsoDate
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.bulk_edit_done
import evoshift.composeapp.generated.resources.bulk_edit_done_partly
import evoshift.composeapp.generated.resources.bulk_edit_undone
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.LocalDate

/** SH-269: a person at a workplace in the Team screen's selection. */
data class MemberKey(val workplaceId: String, val userId: String)

/** SH-269: a person of one of the group's workplaces, as the group-wide list shows them. */
data class BulkMember(val key: MemberKey, val user: User, val workplaceName: String)

/** SH-269: whose people the selection offers: the workplace's, or every workplace of its group (the owner). */
enum class BulkScope { WORKPLACE, GROUP }

/**
 * SH-269: the "Edit selected" sheet as typed: each field off ("leave unchanged") or on with its
 * value. Dates are ISO ("YYYY-MM-DD", as EvoDateField reports them).
 */
data class BulkEditForm(
    val pay: Boolean = false,
    val payKind: PayRaise.Kind = PayRaise.Kind.PERCENT,
    val payValue: String = "",
    val payFrom: String = "",
    val role: Boolean = false,
    val roleValue: String = "",
    val skills: Boolean = false,
    val skillsAdd: Set<String> = emptySet(),
    val skillsRemove: Set<String> = emptySet(),
    val end: Boolean = false,
    val endOn: String = "",
    val contract: Boolean = false,
    val contractType: String? = null,
    val declaredHours: String = "",
    val contractFrom: String = ""
) {
    enum class Problem { NOTHING_CHOSEN, PAY_VALUE, PAY_DATE, ROLE, SKILLS, END_DATE, CONTRACT, CONTRACT_DATE }

    private fun number(text: String): Double? = text.trim().replace(',', '.').toDoubleOrNull()

    /** What stops the sheet from going to the summary (empty: it can). */
    val problems: Set<Problem>
        get() = buildSet {
            if (!pay && !role && !skills && !end && !contract) add(Problem.NOTHING_CHOSEN)
            if (pay) {
                val value = number(payValue)
                if (value == null || !PayRaise(payKind, value, LocalDate(2000, 1, 1)).isValid) add(Problem.PAY_VALUE)
                if (parseIsoDate(payFrom) == null) add(Problem.PAY_DATE)
            }
            if (role && roleValue.isBlank()) add(Problem.ROLE)
            if (skills && skillsAdd.isEmpty() && skillsRemove.isEmpty()) add(Problem.SKILLS)
            if (end && parseIsoDate(endOn) == null) add(Problem.END_DATE)
            if (contract) {
                val hours = declaredHours.trim()
                if (contractType == null && hours.isEmpty()) add(Problem.CONTRACT)
                if (hours.isNotEmpty() && (hours.toIntOrNull() ?: -1) !in 0..400) add(Problem.CONTRACT)
                if (parseIsoDate(contractFrom) == null) add(Problem.CONTRACT_DATE)
            }
        }

    /** The change it describes; null while something is wrong ([problems]). */
    fun toEdit(): BulkEmployeeEdit? {
        if (problems.isNotEmpty()) return null
        return BulkEmployeeEdit(
            payRaise = if (pay) PayRaise(payKind, number(payValue)!!, parseIsoDate(payFrom)!!) else null,
            role = if (role) roleValue.trim() else null,
            skills = if (skills) SkillsEdit.Change(skillsAdd, skillsRemove) else null,
            endOn = if (end) parseIsoDate(endOn) else null,
            contract = if (contract) ContractEdit(contractType, declaredHours.trim().toIntOrNull(), parseIsoDate(contractFrom)!!) else null
        )
    }
}

data class BulkUiState(
    /** Checkboxes on the people (the selection mode). */
    val selecting: Boolean = false,
    val selected: Set<MemberKey> = emptySet(),
    /** The last person toggled: Shift-click selects from it. */
    val anchor: MemberKey? = null,
    val scope: BulkScope = BulkScope.WORKPLACE,
    /** The group the owner may select from (two or more workplaces); null: none offered. */
    val groupName: String? = null,
    val groupUsesDefaultName: Boolean = false,
    /** The people of every workplace of the group (scope GROUP). */
    val groupMembers: List<BulkMember> = emptyList(),
    /** The sheet is open. */
    val editing: Boolean = false,
    val form: BulkEditForm = BulkEditForm(),
    /** Positions and skills the selected people's workplaces know (the sheet's choices). */
    val roles: List<String> = emptyList(),
    val skills: List<String> = emptyList(),
    /** The editor may change pay, contract and end at some selected person's workplace. */
    val canEditPay: Boolean = false,
    /** Each selected workplace's currency (the summary's amounts). */
    val currencies: Map<String, String> = emptyMap(),
    /** The summary before saving. */
    val plan: BulkPlan? = null,
    val isWorking: Boolean = false,
    val message: UiText? = null,
    /** What Undo puts back (after a change, until the next one). */
    val undo: BulkUndo? = null,
    val error: UiText? = null
) {
    val count: Int get() = selected.size
    val workplaceCount: Int get() = selected.map { it.workplaceId }.distinct().size
}

sealed interface BulkEvent {
    data object StartSelecting : BulkEvent
    data object StopSelecting : BulkEvent
    /**
     * A person clicked or tapped in the list: toggled; with Shift (desktop and web) everyone from
     * the last one toggled to this one in [visible] order is selected.
     */
    data class Toggle(val key: MemberKey, val visible: List<MemberKey>, val range: Boolean = false) : BulkEvent
    data class SelectAll(val visible: List<MemberKey>) : BulkEvent
    data object ClearSelection : BulkEvent
    data class SetScope(val scope: BulkScope) : BulkEvent
    data object OpenEdit : BulkEvent
    data object CloseEdit : BulkEvent
    data class UpdateForm(val form: BulkEditForm) : BulkEvent
    data object ShowSummary : BulkEvent
    data object BackToForm : BulkEvent
    data object Save : BulkEvent
    data object Undo : BulkEvent
    data object MessageShown : BulkEvent
}

/**
 * SH-269: the Team screen's selection and "Edit selected": who is selected (at this workplace, or
 * across the owner's group), the sheet, the summary (BulkEditPlanner) and the change
 * (BulkEmployeeEditUseCase) with its Undo.
 */
class BulkEmployeeEditViewModel(
    private val sessionManager: WorkplaceSessionManager,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val getEmployees: GetAssignableEmployeesUseCase,
    private val getWorkplace: GetWorkplaceUseCase,
    private val users: UserRepository,
    private val bulk: BulkEmployeeEditUseCase,
    private val getGroups: GetWorkplaceGroupsUseCase? = null
) : ViewModel() {
    private val _uiState = MutableStateFlow(BulkUiState())
    val uiState: StateFlow<BulkUiState> = _uiState.asStateFlow()

    /** People read for the selection, by key (the workplace's and the group's). */
    private val people = mutableMapOf<MemberKey, User>()
    private val workplaceNames = mutableMapOf<String, String>()

    init {
        viewModelScope.launch {
            sessionManager.currentWorkplaceId.collectLatest { workplaceId ->
                _uiState.value = BulkUiState()
                people.clear()
                if (workplaceId != null) load(workplaceId)
            }
        }
    }

    private suspend fun load(workplaceId: String) {
        getEmployees(workplaceId).getOrNull()?.forEach { people[MemberKey(workplaceId, it.uid)] = it }
        workplaceNames[workplaceId] = getWorkplace(workplaceId).getOrNull()?.name ?: workplaceId
        val me = getCurrentUser().first() ?: return
        // The owner selects across the group of this workplace (two or more workplaces).
        if (me.engagements[workplaceId]?.permission != UserPermission.OWNER) return
        val group = getGroups?.invoke(me.uid)?.getOrNull()?.let { WorkplaceGroups.groupOf(it, workplaceId) } ?: return
        if (group.workplaceIds.size < 2) return
        val members = group.workplaceIds.flatMap { wp ->
            val name = workplaceNames.getOrPut(wp) { getWorkplace(wp).getOrNull()?.name ?: wp }
            getEmployees(wp).getOrNull().orEmpty().map { user ->
                val key = MemberKey(wp, user.uid)
                people[key] = user
                BulkMember(key, user, name)
            }
        }.sortedWith(compareBy({ it.user.displayName.lowercase() }, { it.workplaceName.lowercase() }))
        _uiState.update { it.copy(groupName = group.name, groupUsesDefaultName = group.usesDefaultName, groupMembers = members) }
    }

    fun onEvent(event: BulkEvent) {
        when (event) {
            BulkEvent.StartSelecting -> _uiState.update { it.copy(selecting = true) }
            BulkEvent.StopSelecting -> _uiState.update { it.copy(selecting = false, selected = emptySet(), anchor = null, editing = false, plan = null) }
            is BulkEvent.Toggle -> toggle(event)
            is BulkEvent.SelectAll -> _uiState.update { it.copy(selecting = true, selected = it.selected + event.visible.filterNot(::viaGroup)) }
            BulkEvent.ClearSelection -> _uiState.update { it.copy(selected = emptySet(), anchor = null) }
            is BulkEvent.SetScope -> _uiState.update {
                if (event.scope == BulkScope.GROUP && it.groupMembers.isEmpty()) it
                else it.copy(scope = event.scope, selected = emptySet(), anchor = null)
            }
            BulkEvent.OpenEdit -> openEdit()
            BulkEvent.CloseEdit -> _uiState.update { it.copy(editing = false, plan = null) }
            is BulkEvent.UpdateForm -> _uiState.update { it.copy(form = event.form, plan = null) }
            BulkEvent.ShowSummary -> showSummary()
            BulkEvent.BackToForm -> _uiState.update { it.copy(plan = null) }
            BulkEvent.Save -> save()
            BulkEvent.Undo -> undo()
            BulkEvent.MessageShown -> _uiState.update { it.copy(message = null, error = null) }
        }
    }

    /**
     * SH-268: a group manager's engagement there is the group's (functions/groupManagers.js): it is
     * changed in the workplace groups, never in a bulk edit.
     */
    private fun viaGroup(key: MemberKey): Boolean = people[key]?.engagements?.get(key.workplaceId)?.isViaGroup == true

    private fun toggle(event: BulkEvent.Toggle) {
        if (viaGroup(event.key)) return
        _uiState.update { state ->
            val anchor = state.anchor
            val selected = if (event.range && anchor != null && anchor in event.visible && event.key in event.visible) {
                val (a, b) = listOf(event.visible.indexOf(anchor), event.visible.indexOf(event.key)).sorted()
                state.selected + event.visible.subList(a, b + 1).filterNot(::viaGroup)
            } else if (event.key in state.selected) state.selected - event.key else state.selected + event.key
            state.copy(selecting = true, selected = selected, anchor = event.key)
        }
    }

    private fun rightsAt(me: User, workplaceId: String): Set<Capability> = me.capabilitiesAt(workplaceId, true)

    private fun openEdit() {
        val state = _uiState.value
        if (state.selected.isEmpty()) return
        viewModelScope.launch {
            val me = getCurrentUser().first() ?: return@launch
            val workplaces = state.selected.map { it.workplaceId }.distinct()
            val byWorkplace = workplaces.associateWith { getWorkplace(it).getOrNull()?.settings }
            val settings = byWorkplace.values.filterNotNull()
            _uiState.update {
                it.copy(
                    editing = true,
                    plan = null,
                    roles = settings.flatMap { s -> s.availableRoles }.distinct().sorted(),
                    skills = settings.flatMap { s -> s.workplaceSkills }.distinct().sorted(),
                    canEditPay = workplaces.any { wp -> Capability.MANAGE_PAY in rightsAt(me, wp) },
                    currencies = byWorkplace.mapNotNull { (wp, s) -> s?.let { wp to it.currency } }.toMap()
                )
            }
        }
    }

    /** The summary: every selected person's line, their pay terms read where the editor may see them. */
    private fun showSummary() {
        val state = _uiState.value
        val edit = state.form.toEdit() ?: return
        _uiState.update { it.copy(isWorking = true) }
        viewModelScope.launch {
            runCatching {
                val me = getCurrentUser().first() ?: error("Not signed in")
                val needsTerms = edit.fields.any { it.viaTimeline }
                val targets = state.selected.mapNotNull { key ->
                    val user = people[key] ?: return@mapNotNull null
                    val engagement = user.engagements[key.workplaceId] ?: return@mapNotNull null
                    val terms = if (needsTerms && Capability.MANAGE_PAY in rightsAt(me, key.workplaceId)) payTerms(key) else null
                    BulkTarget(key.userId, user.displayName, key.workplaceId, workplaceNames[key.workplaceId] ?: key.workplaceId, engagement, terms)
                }.sortedWith(compareBy({ it.name.lowercase() }, { it.workplaceName.lowercase() }))
                val closed = targets.map { it.workplaceId }.distinct().associateWith { bulk.closedMonths(it) }
                BulkEditPlanner.plan(edit, targets, { rightsAt(me, it) }, { closed[it].orEmpty() })
            }.fold(
                onSuccess = { plan -> _uiState.update { it.copy(isWorking = false, plan = plan) } },
                onFailure = { e -> _uiState.update { it.copy(isWorking = false, error = e.toUiText()) } }
            )
        }
    }

    private suspend fun payTerms(key: MemberKey): FinancialEngagement? {
        val read = users.getSensitiveUserData(key.userId)
        if (read.exceptionOrNull() is NotFoundException) return null
        return read.getOrNull()?.financialEngagements?.get(key.workplaceId)
    }

    private fun save() {
        val plan = _uiState.value.plan ?: return
        if (plan.isEmpty || _uiState.value.isWorking) return
        _uiState.update { it.copy(isWorking = true) }
        viewModelScope.launch {
            val me = getCurrentUser().first()
            if (me == null) {
                _uiState.update { it.copy(isWorking = false) }
                return@launch
            }
            val result = bulk.apply(plan, me)
            // What was written shows at once: positions and skills of the people read here.
            plan.changing.filter { it.target.workplaceId !in result.failed }.forEach { line ->
                val key = MemberKey(line.target.workplaceId, line.target.userId)
                val user = people[key] ?: return@forEach
                val role = (line.change(com.evoapps.evoshift.domain.team.BulkField.ROLE) as? com.evoapps.evoshift.domain.team.BulkChange.Role)?.after
                val skills = (line.change(com.evoapps.evoshift.domain.team.BulkField.SKILLS) as? com.evoapps.evoshift.domain.team.BulkChange.Skills)?.after
                val engagement = user.engagements[key.workplaceId] ?: return@forEach
                people[key] = user.copy(engagements = user.engagements + (key.workplaceId to engagement.copy(role = role ?: engagement.role, skills = skills ?: engagement.skills)))
            }
            val message = if (result.failed.isEmpty()) {
                UiText.Resource(Res.string.bulk_edit_done, result.people, result.workplaces)
            } else {
                UiText.Resource(Res.string.bulk_edit_done_partly, result.people, result.workplaces, result.failed.size)
            }
            _uiState.update {
                it.copy(
                    isWorking = false,
                    editing = false,
                    plan = null,
                    selecting = false,
                    selected = emptySet(),
                    anchor = null,
                    form = BulkEditForm(),
                    message = message,
                    error = result.failed.values.firstOrNull()?.toUiText(),
                    undo = result.undo.takeUnless { u -> u.isEmpty }
                )
            }
        }
    }

    private fun undo() {
        val undo = _uiState.value.undo ?: return
        _uiState.update { it.copy(undo = null, isWorking = true) }
        viewModelScope.launch {
            val me = getCurrentUser().first() ?: return@launch
            bulk.undo(undo, me).fold(
                onSuccess = { _uiState.update { it.copy(isWorking = false, message = UiText.Resource(Res.string.bulk_edit_undone)) } },
                onFailure = { e -> _uiState.update { it.copy(isWorking = false, error = e.toUiText()) } }
            )
        }
    }
}
